import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldIcon, LockIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { HOME, PRACTICE, ABOUT, SERVICES, FAQ_CONTENT } from '../content/site';
import { Section, SectionHeading, Eyebrow, ButtonLink, ArrowLink, Reveal, EASE } from '../components/ui/Primitives';
import { ServiceGrid } from '../components/blocks/ServiceGrid';
import { ProcessSteps } from '../components/blocks/ProcessSteps';
import { FaqAccordion } from '../components/blocks/FaqAccordion';
const CRED_ICONS: Record<string, React.ElementType> = {
  shield: ShieldIcon,
  lock: LockIcon
};
export function Home() {
  const {
    language
  } = useLanguage();
  const t = HOME[language];
  const about = ABOUT[language];
  const services = SERVICES[language];
  const faq = FAQ_CONTENT[language];
  return <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream px-5 pb-20 pt-12 sm:px-8 lg:px-10 lg:pb-28 lg:pt-16" data-id="element-252" data-mp="3ovkhdx0aay9c">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-champagne/30 blur-3xl" data-id="element-253" data-mp="30w04u1s2zm4k" />
        <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-254" data-mp="2qxkdbxp56auj">
          <motion.div initial={{
          opacity: 0,
          y: 28
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          ease: EASE
        }} className="lg:col-span-7" data-id="element-255" data-mp="2mw8nbr2cvnlt">
            <Eyebrow data-id="element-256" data-mp="2d5w3y26a20h9">{t.hero.eyebrow}</Eyebrow>

            <h1 className="mt-6 font-serif text-[2.75rem] font-light leading-[1.02] tracking-[-0.02em] text-espresso sm:text-6xl lg:text-[4.25rem]" data-id="element-257" data-mp="287krdjollbvc">
              {t.hero.headline}
            </h1>

            <p className="mt-7 max-w-xl text-base font-light leading-relaxed text-espresso/65 lg:text-lg" data-id="element-258" data-mp="1z7xpyxvvc62a">
              {t.hero.sub}
            </p>

            <div className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center" data-id="element-259" data-mp="1u94l1tundndq">
              <ButtonLink to="/contact" data-id="element-260" data-mp="1kjfi9cbcv0ls">{t.hero.cta}</ButtonLink>
              <ArrowLink to="/services" data-id="element-261" data-mp="1omkif87yqq79">{t.hero.ctaSecondary}</ArrowLink>
            </div>

            <ul className="mt-12 flex flex-wrap gap-x-6 gap-y-3 border-t border-espresso/10 pt-8" data-id="element-262" data-mp="1ancigd3z7e20">
              {t.hero.badges.map(badge => <li key={badge} className="flex items-center gap-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-espresso/50" data-id="element-263" data-mp="1fnqgd2bv57mb">
                  <span aria-hidden="true" className="h-1 w-1 rounded-full bg-bronze" data-id="element-264" data-mp="289i6tig29ofn" />
                  {badge}
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.1,
          delay: 0.2,
          ease: EASE
        }} className="relative lg:col-span-5" data-id="element-265" data-mp="2m9klywwzi3r7">
            <div aria-hidden="true" className="absolute -right-4 -top-4 h-full w-full rounded-[1.75rem] border border-bronze/30 lg:-right-6 lg:-top-6" data-id="element-266" data-mp="fjzgxothicdq" />
            <figure className="relative overflow-hidden rounded-[1.75rem] bg-espresso" data-id="element-267" data-mp="odmb5ai1n7kt">
              <img src={PRACTICE.heroImage} alt={t.hero.imageCaption} className="h-full w-full object-cover" data-id="element-268" data-mp="kdtvyey5a36h" />
              <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-espresso/85 to-transparent" data-id="element-269" data-mp="3nu10enodw4gb" />
              <figcaption className="absolute inset-x-0 bottom-0 flex items-center gap-3 p-6" data-id="element-270" data-mp="ot8kv6x21h8">
                <span aria-hidden="true" className="h-px w-6 bg-bronze" data-id="element-271" data-mp="1h2t9a8dl5v5j" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-pearl" data-id="element-272" data-mp="1h4w6pdh2iqka">
                  {t.hero.imageCaption}
                </span>
              </figcaption>
            </figure>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className="border-y border-espresso/10 bg-white px-5 py-8 sm:px-8 lg:px-10" data-id="element-273" data-mp="2cdhspogtqvmq">
        <dl className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-8 lg:grid-cols-4" data-id="element-274" data-mp="2hb9jgf5qgbpc">
          {t.trustStrip.map(item => <div key={item.label} data-id="element-275" data-mp="2m8t0fmttf2kz">
              <dt className="font-serif text-lg font-light text-espresso" data-id="element-276" data-mp="2qdafx9t56tpy">
                {item.label}
              </dt>
              <dd className="mt-1 text-[10px] font-medium uppercase tracking-[0.14em] text-espresso/45" data-id="element-277" data-mp="2vaj2dmlbpjx0">
                {item.detail}
              </dd>
            </div>)}
        </dl>
      </div>

      {/* ABOUT */}
      <Section tone="cream" data-id="element-278" data-mp="353q5f0slcntx">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-20" data-id="element-279" data-mp="3a10313q4w5su">
          <div className="lg:col-span-6" data-id="element-280" data-mp="3a2kx6s8jdmq6">
            <SectionHeading eyebrow={t.aboutTeaser.eyebrow} title={t.aboutTeaser.headline} data-id="element-281" data-mp="3e1tqxx3ktklq" />
            <Reveal delay={0.1} data-id="element-282" data-mp="12frjcps4rcbn">
              <p className="mt-6 text-base font-light leading-relaxed text-espresso/70" data-id="element-283" data-mp="12cn8j9k5tic0">
                {about.body}
              </p>
              <div className="mt-9" data-id="element-284" data-mp="tds2pyl12a98">
                <ArrowLink to="/about" data-id="element-285" data-mp="tetoawzo6f57">{t.aboutTeaser.link}</ArrowLink>
              </div>
            </Reveal>
          </div>

          <div className="flex flex-col justify-center gap-5 lg:col-span-6" data-id="element-286" data-mp="ohjrgw9yb17c">
            {about.credentials.map((cred, index) => {
            const Icon = CRED_ICONS[cred.icon] || ShieldIcon;
            return <Reveal key={cred.title} delay={0.15 + index * 0.1} data-id="element-287" data-mp="1ccwa72wx7vys" data-mpi={index}>
                  <div className="flex items-start gap-5 rounded-2xl border border-espresso/10 bg-white p-7" data-id="element-288" data-mp="1cb2zncj6srli">
                    <div className="mt-1 shrink-0 text-bronze" data-id="element-289" data-mp="17c8gual7z31e">
                      <Icon className="h-5 w-5" strokeWidth={1.25} data-id="element-290" data-mp="24v1iob4y6k5s" />
                    </div>
                    <div data-id="element-291" data-mp="2015q5o7gdpi0">
                      <h3 className="font-serif text-lg font-normal text-espresso" data-id="element-292" data-mp="200no2ay40h26">
                        {cred.title}
                      </h3>
                      <p className="mt-2 text-sm font-light leading-relaxed text-espresso/60" data-id="element-293" data-mp="271n0eerwoxr">
                        {cred.description}
                      </p>
                    </div>
                  </div>
                </Reveal>;
          })}
          </div>
        </div>
      </Section>

      {/* SERVICES */}
      <Section tone="sand" data-id="element-294" data-mp="3al1xng6bfk7v">
        <div className="mb-14 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-295" data-mp="3aoxzdtwxunrg">
          <SectionHeading eyebrow={t.servicesTeaser.eyebrow} title={t.servicesTeaser.headline} sub={t.servicesTeaser.sub} data-id="element-296" data-mp="3fjbstqs1ikth" />
          <div className="shrink-0 pb-2" data-id="element-297" data-mp="36ljiz25385dh">
            <ArrowLink to="/services" data-id="element-298" data-mp="zutldbpkhw6x">{t.servicesTeaser.link}</ArrowLink>
          </div>
        </div>
        <ServiceGrid data-id="element-299" data-mp="14td6j9wc73y8" />
        <Reveal delay={0.1} data-id="element-300" data-mp="14two2pi2rqbq">
          <p className="mt-8 max-w-2xl text-xs font-light leading-relaxed text-espresso/45" data-id="element-301" data-mp="14t4x5rzfagrs">
            {services.note}
          </p>
        </Reveal>
      </Section>

      {/* PROCESS */}
      <Section tone="espresso" data-id="element-302" data-mp="19zs77tfih5vu">
        <div className="mb-14 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-303" data-mp="19wnrbysoolpc">
          <SectionHeading eyebrow={t.processTeaser.eyebrow} title={t.processTeaser.headline} tone="light" data-id="element-304" data-mp="1nrq7der8vrfv" />
          <div className="shrink-0 pb-2" data-id="element-305" data-mp="1xn0bq9548awt">
            <ArrowLink to="/process" tone="light" data-id="element-306" data-mp="1xpw7prxekpim">
              {t.processTeaser.link}
            </ArrowLink>
          </div>
        </div>
        <ProcessSteps tone="dark" data-id="element-307" data-mp="26p15ux51ygu9" />
      </Section>

      {/* PRACTICE STATEMENT */}
      <Section tone="white" data-id="element-308" data-mp="22nfmqu1xd6bm">
        <Reveal data-id="element-309" data-mp="22o7eftseijwm">
          <div className="mx-auto max-w-3xl text-center" data-id="element-310" data-mp="22nny2hieg3kp">
            <div className="flex justify-center" data-id="element-311" data-mp="ca6xj3j5sq8c">
              <Eyebrow data-id="element-312" data-mp="cb0526pxzjrj">{about.statementTitle}</Eyebrow>
            </div>
            <p className="mt-8 font-serif text-lg font-light italic leading-relaxed text-espresso/75 lg:text-xl" data-id="element-313" data-mp="8aganawr6u93">
              {about.statement}
            </p>
          </div>
        </Reveal>
      </Section>

      {/* FAQ */}
      <Section tone="cream" data-id="element-314" data-mp="11r1vv20neh1b">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-20" data-id="element-315" data-mp="16k4rrgswe2ko">
          <div className="lg:col-span-4" data-id="element-316" data-mp="16ko8iu6takx4">
            <div className="lg:sticky lg:top-32" data-id="element-317" data-mp="1alzrigv6k7wc">
              <SectionHeading eyebrow={t.faqTeaser.eyebrow} title={t.faqTeaser.headline} sub={faq.sub} data-id="element-318" data-mp="1akoo4i5nd25r" />
              <div className="mt-8" data-id="element-319" data-mp="ivbrx995naks">
                <ArrowLink to="/faq" data-id="element-320" data-mp="iwei1eynl8zn">{t.faqTeaser.link}</ArrowLink>
              </div>
            </div>
          </div>
          <div className="lg:col-span-8" data-id="element-321" data-mp="my8cv6m63irw">
            <FaqAccordion limit={4} data-id="element-322" data-mp="rw1ju1vaogt8" />
          </div>
        </div>
      </Section>

      {/* CTA BAND */}
      <Section tone="espresso" data-id="element-323" data-mp="1ya0yhlcpng9n">
        <Reveal data-id="element-324" data-mp="1yd584y8y347p">
          <div className="mx-auto max-w-2xl text-center" data-id="element-325" data-mp="23a5e795so6yt">
            <div className="flex justify-center" data-id="element-326" data-mp="238khpdzx6dyb">
              <Eyebrow tone="light" data-id="element-327" data-mp="2ddssdfz69orc">{t.contactTeaser.eyebrow}</Eyebrow>
            </div>
            <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-pearl sm:text-4xl lg:text-[2.75rem]" data-id="element-328" data-mp="28ezehmfz3vqn">
              {t.contactTeaser.headline}
            </h2>
            <p className="mt-5 text-base font-light leading-relaxed text-pearl/65" data-id="element-329" data-mp="23lwjrazgmg8v">
              {t.contactTeaser.sub}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-5 sm:flex-row" data-id="element-330" data-mp="1unulh37421pa">
              <ButtonLink to="/contact" variant="light" data-id="element-331" data-mp="1po890vz3wphj">
                {t.contactTeaser.cta}
              </ButtonLink>
              <Link to="/contact" className="text-[11px] font-light tracking-wide text-pearl/55 underline underline-offset-4 transition-colors hover:text-pearl" data-id="element-332" data-mp="1kq6gzljpdftx">
                {PRACTICE.address}
              </Link>
            </div>
          </div>
        </Reveal>
      </Section>
    </>;
}